// Angular
import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
// Loading bar
import { LoadingBarService } from '@ngx-loading-bar/core';
// RxJS
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { AuthService, Logout } from '../../../auth';
import { UsersPageToggleLoading } from '../../../auth';
import { AppState } from '../../../reducers';
import {
  AreaGroupPageToggleLoading,
  AreaPageToggleLoading,
  BlacklistPageToggleLoading,
  ExternalShipmentPageToggleLoading,
  InvoiceCheckReportPageToggleLoading,
  KgCheckReportPageToggleLoading,
  LiquidationPageToggleLoading,
  ShipmentsPageToggleLoading,
  UserfilesPageToggleLoading,
  UserwfdPageToggleLoading,
} from '../../../rex';

@Injectable()
export class InterceptService implements HttpInterceptor {
  isRefreshing = false;
  RefreshLimit = 2;
  refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private auth: AuthService,
    private router: Router,
    private http: HttpClient,
    private store: Store<AppState>,
    private loader: LoadingBarService
  ) {}

  // intercept request and add token
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.headers.get('skip')) {
      return next.handle(request);
    }

    // First wait for any preventive refresh to complete (following Psaltis pattern)
    return this.refreshTokenIfExpiresSoon().pipe(
      filter(res => res !== 'wait'),
      takeWhile(res => res !== 'cancel'),
      take(1),
      delay(0),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      switchMap(res => {
        return next.handle(this.addAuthenticationToken(request)).pipe(
          catchError(error => {
            if ([401, 403].includes(error.status)) {
              if (this.isRefreshing) {
                return this.refreshTokenSubject.pipe(
                  filter(res => res !== 'wait'),
                  takeWhile(res => res !== 'cancel'),
                  take(1),
                  switchMap(() =>
                    next.handle(this.addAuthenticationToken(request))
                  )
                );
              } else {
                this.isRefreshing = true;
                this.refreshTokenSubject.next('wait');

                return this.refreshAccessToken().pipe(
                  switchMap((token: any) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(token);

                    if (token) {
                      return next.handle(this.addAuthenticationToken(request));
                    } else {
                      this.isRefreshing = false;
                      this.logout();
                      return EMPTY;
                    }
                  }),

                  catchError(err => {
                    this.isRefreshing = false;
                    this.logout();
                    return throwError(err);
                  })
                );
              }
            } else {
              return throwError(error);
            }
          })
        );
      }),
      delay(0)
    );
  }

  addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = localStorage.getItem('accessToken');

    if (!token) {
      return request;
    } else {
      return request.clone({
        setHeaders: {
          Authorization: `JWT ${token}`,
        },
      });
    }
  }
  refreshTokenIfExpiresSoon(): Observable<any> {
    const token = localStorage.getItem('accessToken');

    if (this.needsRefresh(token) && !this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshAccessToken()
        .pipe(
          take(1),
          tap(res => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(res);
          })
        )
        .subscribe();
    }

    if (this.isRefreshing) {
      this.refreshTokenSubject.next('wait');
    } /*else {
      this.refreshTokenSubject.next('run');
    }*/

    return this.refreshTokenSubject;
  }

  refreshAccessToken(): Observable<any> {
    const refs_token = localStorage.getItem('refreshToken');

    if (!refs_token) {
      this.logout();
      return of(null);
    }

    //need to check for bad return
    const httpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      skip: 'true',
    });
    return this.http
      .post<any>(
        'api/api-token-refresh/',
        { refresh: refs_token },
        { headers: httpHeaders }
      )
      .pipe(
        map((res: any) => {
          localStorage.setItem('accessToken', res.access);
          return of(res.access);
        }),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        catchError(err => {
          this.logout();
          return of(null);
        })
      );
  }

  logout() {
    // Reset all loading states and refresh flags
    this.isRefreshing = false;
    // Emit a cancel to cancel any pending requests
    this.refreshTokenSubject.next('cancel');
    // Immediately re-emit 'null to reset the subject
    this.refreshTokenSubject.next(null);

    // Complete any pending loading operations
    this.loader.complete();

    // Clear all page loading states
    this.store.dispatch(new AreaPageToggleLoading({ isLoading: false }));
    this.store.dispatch(new AreaGroupPageToggleLoading({ isLoading: false }));
    this.store.dispatch(new BlacklistPageToggleLoading({ isLoading: false }));
    this.store.dispatch(
      new ExternalShipmentPageToggleLoading({ isLoading: false })
    );
    this.store.dispatch(
      new InvoiceCheckReportPageToggleLoading({ isLoading: false })
    );
    this.store.dispatch(
      new KgCheckReportPageToggleLoading({ isLoading: false })
    );
    this.store.dispatch(new LiquidationPageToggleLoading({ isLoading: false }));
    this.store.dispatch(new ShipmentsPageToggleLoading({ isLoading: false }));
    this.store.dispatch(new UserfilesPageToggleLoading({ isLoading: false }));
    this.store.dispatch(new UserwfdPageToggleLoading({ isLoading: false }));
    this.store.dispatch(new UsersPageToggleLoading({ isLoading: false }));

    // Clear the auth state
    this.store.dispatch(new Logout());
  }

  parseJwt(token: any) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  needsRefresh(token: string | null): boolean {
    if (!token) {
      return false;
    }

    try {
      const payload = this.parseJwt(token);
      const exp = new Date(0);
      exp.setUTCSeconds(payload.exp);
      const now = new Date(Date.now());
      const diff = exp.getTime() - now.getTime();
      const minsLeft = Math.round(diff / 60000); // Direct conversion: ms to minutes
      return minsLeft <= this.RefreshLimit;
    } catch (error) {
      return false;
    }
  }
}
